// src/pages/shared/CompanyCertificates.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../api/axios";
import { toast } from "react-toastify";
import { toAbsoluteUrl } from "../../utils/getAssetUrl";
import useAuth from "../../hooks/useAuth";
import "./CompanyCertificates.scss";

export default function CompanyCertificates() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const role = user?.role || "";
    const isEmployee = role === "employee";

    useEffect(() => {
        if (isEmployee) {
            navigate("/work");
        }
    }, [isEmployee, navigate]);

    const [items, setItems] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [viewerDoc, setViewerDoc] = useState(null);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({ title: "", file: null, notes: "" });

    const level = user?.level || 1;


    useEffect(() => {
        load();
    }, []);

    const load = async () => {
        try {
            const { data } = await api.get("/certificates");
            setItems(Array.isArray(data) ? data : []);
        } catch {
            toast.error("Sertifikalar yüklenemedi");
        }
    };

    const isCustomer = role === "customer";
    const isAdmin = role === "admin";
    const isLevel1 = role === "employee" && level === 1;
    const canManage = isAdmin || isLevel1;
    const userToken = localStorage.getItem("accessToken");

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!form.title || !form.file) {
            return toast.warning("Lütfen başlık ve dosya seçin");
        }

        const fd = new FormData();
        fd.append("title", form.title);
        fd.append("file", form.file);
        if (form.notes) fd.append("notes", form.notes);

        setLoading(true);
        try {
            await api.post("/certificates", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Sertifika başarıyla eklendi");
            setShowModal(false);
            setForm({ title: "", file: null, notes: "" });
            load();
        } catch (err) {
            toast.error(err.response?.data?.error || "Yükleme hatası");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id, title) => {
        if (!window.confirm(`"${title}" silinecek, emin misiniz?`)) return;
        try {
            await api.delete(`/certificates/${id}`);
            toast.success("Sertifika silindi");
            load();
        } catch {
            toast.error("Silinemedi");
        }
    };

    const handleDownload = async (item) => {
        try {
            const resp = await api.get(`/certificates/${item.id}/download`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([resp.data]));
            const link = document.createElement("a");
            link.href = url;

            let ext = "";
            if (item.mime) {
                if (item.mime.includes("pdf")) ext = ".pdf";
                else if (item.mime.includes("png")) ext = ".png";
                else if (item.mime.includes("jpeg") || item.mime.includes("jpg")) ext = ".jpg";
                else if (item.mime.includes("word") || item.mime.includes("officedocument")) ext = ".docx";
            }
            // Fallback: check title for extension
            if (!ext && item.title.toLowerCase().includes(".pdf")) ext = ".pdf";
            
            const filename = `${item.title.replace(/[^a-z0-9\ı\ö\ü\ş\ğ\ç]/gi, '_').toLowerCase()}${ext || '.pdf'}`;

            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch {
            toast.error("Dosya indirilemedi");
        }
    };

    return (
        <Layout title="Şirket Sertifikaları">
            <div className="company-certs">
                <div className="head">
                    <h2>Şirket Sertifikaları</h2>
                    {canManage && (
                        <button className="btn primary" onClick={() => setShowModal(true)}>
                            + Yeni Sertifika
                        </button>
                    )}
                </div>

                <div className="certs-grid">
                    {items.length === 0 && <div className="empty">Henüz yüklenmiş sertifika yok.</div>}

                    {items.map((it) => (
                        <div key={it.id} className="cert-card" onClick={() => setViewerDoc(it)} style={{ cursor: 'pointer' }}>
                            <div className="c-info">
                                <h3>{it.title}</h3>
                                {it.notes && <p className="notes">{it.notes}</p>}
                                {!isCustomer && (
                                    <div className="meta">
                                        {new Date(it.uploadedAt).toLocaleString("tr-TR")}
                                    </div>
                                )}
                            </div>
                            <div className="c-preview">
                                {it.file ? (
                                    it.mime?.startsWith("image/") ? (
                                        <img
                                            src={`${toAbsoluteUrl(`api/certificates/${it.id}/view`, { forceApi: true })}?token=${userToken}`}
                                            alt={it.title}
                                            className="preview-frame"
                                            style={{ objectFit: "contain" }}
                                        />
                                    ) : (
                                        <iframe
                                            src={`${toAbsoluteUrl(`api/certificates/${it.id}/view`, { forceApi: true })}?token=${userToken}#toolbar=0&navpanes=0`}
                                            className="preview-frame"
                                            title={it.title}
                                            style={{ pointerEvents: 'none' }}
                                        />
                                    )
                                ) : (
                                    <div className="empty">Dosya yok.</div>
                                )}
                            </div>
                            <div className="c-actions">
                                <button className="btn ghost" onClick={(e) => { e.stopPropagation(); handleDownload(it); }}>
                                    İndir
                                </button>
                                {canManage && (
                                    <button className="btn danger" onClick={(e) => { e.stopPropagation(); handleDelete(it.id, it.title); }}>
                                        Sil
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Ekleme Modalı */}
                {showModal && canManage && (
                    <div className="modal-backdrop" onClick={() => setShowModal(false)}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>Sertifika Yükle</h3>
                                <button className="close-btn" onClick={() => setShowModal(false)}>&times;</button>
                            </div>
                            <form className="modal-body" onSubmit={handleUpload}>
                                <div className="form-group">
                                    <label>Belge Başlığı *</label>
                                    <input
                                        type="text"
                                        required
                                        value={form.title}
                                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Notlar (Opsiyonel)</label>
                                    <textarea
                                        rows={2}
                                        value={form.notes}
                                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Dosya (PDF, JPG, PNG, DOCX) *</label>
                                    <input
                                        type="file"
                                        required
                                        onChange={(e) => setForm({ ...form, file: e.target.files[0] })}
                                    />
                                </div>
                                <div className="form-actions" style={{ marginTop: "1rem", display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                                    <button type="button" className="btn ghost" onClick={() => setShowModal(false)}>İptal</button>
                                    <button type="submit" className="btn primary" disabled={loading}>
                                        {loading ? "Yükleniyor..." : "Yükle"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
                {/* Görüntüleme Modalı */}
                {viewerDoc && (
                    <div className="modal-backdrop viewer-backdrop" onClick={() => setViewerDoc(null)}>
                        <div className="viewer-modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="viewer-header">
                                <h3>{viewerDoc.title}</h3>
                                <button className="close-btn" onClick={() => setViewerDoc(null)}>&times;</button>
                            </div>
                            <div className="viewer-body">
                                {viewerDoc.mime?.startsWith("image/") ? (
                                    <img
                                        src={`${toAbsoluteUrl(`api/certificates/${viewerDoc.id}/view`, { forceApi: true })}?token=${userToken}`}
                                        alt={viewerDoc.title}
                                    />
                                ) : (
                                    <iframe
                                        src={`${toAbsoluteUrl(`api/certificates/${viewerDoc.id}/view`, { forceApi: true })}?token=${userToken}#toolbar=0&navpanes=0`}
                                        title={viewerDoc.title}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}
